import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect } from 'react';
import SidebarPageLayout from '../components/layout/SidebarPageLayout';

function PrivacyPolicy() {

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub - Privacy Policy';
  }, []);

  return (
    <SidebarPageLayout>
      <div className="content p-5" style={{ maxWidth: '52rem' }}>
        <h1 className="mb-3">Privacy Policy</h1>
        <p className="text-muted mb-5">Last updated: August 11, 2026</p>

        <p className="mb-4">
          This Privacy Policy describes how VivoSol (&quot;VivoSol,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
          collects, uses, and shares information in connection with the VivoSol Plant Performance
          Insights Hub and related services (the &quot;Service&quot;).
        </p>

        <h2 className="h4 mt-4">1. Who this policy covers</h2>
        <p>
          The Service is designed for business customers who operate or monitor energy assets.
          This policy applies to individuals who access the Service on behalf of a customer
          organization (for example, operators, analysts, and administrators). Where we process
          information solely on a customer&apos;s instructions, that customer is typically the
          controller of the data and their own privacy notice may also apply.
        </p>

        <h2 className="h4 mt-4">2. Information we collect</h2>
        <p>Depending on how the Service is configured, we may collect:</p>
        <ul>
          <li>
            <strong>Account and identity information</strong> — name, work email address,
            organization, role, and authentication details (including information provided
            through our identity provider, such as Auth0).
          </li>
          <li>
            <strong>Usage and device information</strong> — log data, IP address, browser type,
            pages viewed, feature usage, timestamps, and similar diagnostics needed to operate
            and secure the Service.
          </li>
          <li>
            <strong>Customer content and operational data</strong> — plant, portfolio, and
            equipment telemetry, configuration, reports, and other data that customers or their
            systems upload or connect to the Service. This is typically business operational
            data rather than consumer personal data.
          </li>
          <li>
            <strong>Support communications</strong> — messages, attachments, and related
            details you send when requesting help.
          </li>
        </ul>
        <p>
          We do not intentionally collect special categories of personal data (such as health
          or biometric data) through the Service.
        </p>

        <h2 className="h4 mt-4">3. How we use information</h2>
        <p>We use information to:</p>
        <ul>
          <li>Provide, maintain, and improve the Service;</li>
          <li>Authenticate users and manage access;</li>
          <li>Monitor performance, reliability, and security;</li>
          <li>Respond to support requests and communicate about the Service;</li>
          <li>Comply with law and enforce our agreements; and</li>
          <li>Create aggregated or de-identified insights that do not identify an individual.</li>
        </ul>

        <h2 className="h4 mt-4">4. How we share information</h2>
        <p>We may share information with:</p>
        <ul>
          <li>
            <strong>Service providers</strong> who help us host, authenticate, analyze, or
            support the Service (for example cloud infrastructure and identity providers),
            under contractual confidentiality and security obligations;
          </li>
          <li>
            <strong>Your organization</strong> and other authorized users within your account;
          </li>
          <li>
            <strong>Professional advisors</strong> (legal, security, auditors) where needed; and
          </li>
          <li>
            <strong>Authorities</strong> when required by law, legal process, or to protect
            rights, safety, or security.
          </li>
        </ul>
        <p>
          We do not sell personal information. If we are involved in a merger, acquisition, or
          asset sale, information may transfer as part of that transaction subject to
          appropriate safeguards.
        </p>

        <h2 className="h4 mt-4">5. Cookies and similar technologies</h2>
        <p>
          We and our providers may use cookies, local storage, and similar technologies for
          authentication, session management, preferences, and security. You can control
          cookies through your browser settings; disabling some cookies may limit Service
          functionality.
        </p>

        <h2 className="h4 mt-4">6. Data retention</h2>
        <p>
          We retain information for as long as needed to provide the Service, meet customer
          contractual requirements, resolve disputes, and comply with legal obligations.
          Retention periods vary by data type and customer agreement. When no longer needed,
          we delete or de-identify information in accordance with our practices and
          commitments to customers.
        </p>

        <h2 className="h4 mt-4">7. Security</h2>
        <p>
          We use administrative, technical, and organizational measures designed to protect
          information against unauthorized access, loss, or alteration. No method of
          transmission or storage is completely secure; we encourage customers to use strong
          credentials, role-based access, and secure network practices.
        </p>

        <h2 className="h4 mt-4">8. International transfers</h2>
        <p>
          Information may be processed in the United States or other countries where we or our
          providers operate. Where required, we use appropriate transfer mechanisms and
          contractual safeguards.
        </p>

        <h2 className="h4 mt-4">9. Your choices and rights</h2>
        <p>
          Depending on your location and applicable law (including CCPA/CPRA, GDPR, and similar
          frameworks), you may have rights to access, correct, delete, or restrict processing
          of personal information, or to object to certain processing. Because the Service is
          typically provided to organizations, please contact your company administrator first;
          we will work with your organization to address verified requests. You may also have
          the right to lodge a complaint with a supervisory authority.
        </p>

        <h2 className="h4 mt-4">10. Children</h2>
        <p>
          The Service is not directed to individuals under 16, and we do not knowingly collect
          personal information from children.
        </p>

        <h2 className="h4 mt-4">11. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the revised version
          on this page and update the &quot;Last updated&quot; date. Continued use of the Service after
          changes become effective constitutes acceptance of the updated policy, except where
          applicable law requires additional notice or consent.
        </p>

        <h2 className="h4 mt-4">12. Contact us</h2>
        <p>
          For privacy questions or requests, contact{' '}
          <a href="mailto:jngai@ennovara.com">Judy Ngai, jngai@ennovara.com</a>.
        </p>
      </div>
    </SidebarPageLayout>
  );
}

export default PrivacyPolicy;
